import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createCampaign } from '../api/campaign';
import { getCompanyPlans } from '../api/plan';
import { usePlans } from '../utils/usePlans.js';
import RegistarLayout from '../components/RegistarLayout';
import InputFormLayout from '../components/InputFormLayout';
import RegistarButtonLayout from '../components/RegistarButtonLayout';
import SelectLayout from '../components/SelectLayout';
import './CampaignRegistar.css';

const CampaignRegistration = () => {
  const [campaignCode, setCampaignCode] = useState('');
  const [planName, setPlanName] = useState('');
  const [priceId, setPriceId] = useState('');
  const [repeatplanId, setRepeatplanId] = useState('');
  const [error, setError] = useState(null);
  const [planType, setPlanType] = useState('premium');
  const navigate = useNavigate();
  const { loading } = usePlans();
  const [upgradePlanId, setUpgradePlanId] = useState('');
  const [upgradeType, setUpgradeType] = useState('0');
  const [companyPlans, setCompanyPlans] = useState([]);
  const { companyId } = useParams();
  const [upgradePlan, setUpgradePlan] = useState([]);
  const [usageDays, setUsageDays] = useState('');
  const selectedPlan = companyPlans.find((p) => p.price_id === priceId);
  const [exclusivePlan, setExclusivePlan] = useState('');
  const [referrerUrls, setReferrerUrls] = useState('');
  const [isMember, setIsMember] = useState('0'); // '0': なし, '1': 手動, '2': 自動
  const [memberIdValidationType, setMemberIdValidationType] = useState('');
  const [memberIdMaxDigits, setMemberIdMaxDigits] = useState('');
  const [memberIdMinDigits, setMemberIdMinDigits] = useState('');
  const [memberIdLabel, setMemberIdLabel] = useState('');

  // バリデーションチェックNoのオプション（1-15）
  const validationTypeOptions = [
    { value: 1, label: '01:数字のみ' },
    { value: 2, label: '02:英小文字のみ' },
    { value: 3, label: '03:英大文字のみ' },
    { value: 4, label: '04:記号のみ' },
    { value: 5, label: '05:数字、英小文字' },
    { value: 6, label: '06:数字、英大文字' },
    { value: 7, label: '07:数字、記号' },
    { value: 8, label: '08:英小文字、英大文字' },
    { value: 9, label: '09:英小文字、記号' },
    { value: 10, label: '10:英大文字、記号' },
    { value: 11, label: '11:数字、英小文字、英大文字' },
    { value: 12, label: '12:数字、英小文字、記号' },
    { value: 13, label: '13:数字、英大文字、記号' },
    { value: 14, label: '14:英小文字、英大文字、記号' },
    { value: 15, label: '15:数字、英小文字、英大文字、記号' },
  ];
  useEffect(() => {
    if (companyId) {
      fetchCompanyPlans(companyId);
    }
  }, [companyId]);

  const fetchCompanyPlans = async (companyId) => {
    try {
      const plans = await getCompanyPlans(companyId);
      setCompanyPlans(plans.plans);
    } catch (error) {
      console.error('アップグレードプラン取得エラー:', error);
    }
  };

  useEffect(() => {
    if (!priceId) {
      setUpgradePlan([]);
      return;
    }

    const upgradePlanOptions = companyPlans.filter(
      (p) => p.price_id !== priceId
    );
    setUpgradePlan(upgradePlanOptions);
  }, [priceId, companyPlans]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      await createCampaign(
        companyId,
        campaignCode,
        planName,
        priceId,
        planType,
        upgradePlanId,
        upgradeType,
        selectedPlan?.interval === 'one_time' ? usageDays : null,
        exclusivePlan,
        selectedPlan?.trial_days > 0 ? repeatplanId : null,
        referrerUrls,
        Number(isMember), // 0: なし, 1: 手動, 2: 自動
        memberIdValidationType ? Number(memberIdValidationType) : null,
        memberIdMaxDigits ? Number(memberIdMaxDigits) : null,
        memberIdMinDigits ? Number(memberIdMinDigits) : null,
        memberIdLabel || null
      );

      navigate('/campaignlist');
    } catch (err) {
      console.error(err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        console.error('バリデーションエラー内容:', err.response.data);
        setError('登録に失敗しました。');
      }
    }
  };

  if (loading) return <p>プランを読み込み中...</p>;

  return (
    <RegistarLayout title="キャンペーン登録画面" margin="20px auto">
        {error && <p className="error">{error}</p>}

        <InputFormLayout
            id="campaignName"
            labelTitle="キャンペーン名"
            type="text"
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
            placeholder="例: FreeCampaign"
          />
          
          <InputFormLayout
            id="campaignCode"
            labelTitle="キャンペーンコード"
            type="text"
            value={campaignCode}
            onChange={(e) => setCampaignCode(e.target.value)}
            placeholder="例: 1234"
          />

          <SelectLayout
            id="campaignPlan"
            selectTitle="キャンペーンプラン"
            value={priceId}
            onChange={(e) => setPriceId(e.target.value)}
            options={[...companyPlans].reverse().map(plan => ({
              id: plan.price_id,
              name: plan.name,
              value: plan.price_id
            }))}
          />

          {selectedPlan?.interval === 'one_time' && (
            <InputFormLayout
              id="usageDays"
              labelTitle="使用期間（日数）"
              type="number"
              min="1"
              value={usageDays}
              onChange={(e) => setUsageDays(Number(e.target.value))}
              placeholder="例: 30"
            />
          )}

          {selectedPlan?.trial_days > 0 && (
            <SelectLayout
              id="repeatPlan"
              selectTitle="トライアル終了後再契約プラン"
              value={repeatplanId}
              onChange={(e) => setRepeatplanId(e.target.value)}
              options={companyPlans.map(plan => ({
                id: plan.price_id,
                name: plan.name,
                value: plan.price_id
              }))}
            />
          )}
          
          <SelectLayout
            id="upgradeType"
            selectTitle="アップグレードタイプ"
            value={upgradeType}
            onChange={(e) => setUpgradeType(e.target.value)}
            options={[
              { value: "0", name: "なし" },
              { value: "1", name: "1" },
              { value: "2", name: "2" }
            ]}
            show={false}
          />

          {upgradeType !== '0' && (
            <SelectLayout
            id="upgradePlanId"
            selectTitle="アップグレード先プランコード"
            value={upgradePlanId}
            onChange={(e) => setUpgradePlanId(e.target.value)}
            options={upgradePlan.map(plan => ({
              id: plan.id,
              name: plan.name,
              value: plan.price_id
            }))}
          />
          )}
          <SelectLayout
              id="exclusivePlan"
              selectTitle="同時契約不可プラン"
              value={exclusivePlan || ''}
              onChange={(e) => setExclusivePlan(e.target.value)}
              options={upgradePlan?.map(plan => ({
                id: plan.price_id,
                name: plan.name,
                value: plan.price_id
              }))}
            />

          <SelectLayout
            id="planSelect"
            selectTitle="料金プラン"
            value={planType}
            onChange={(e) => setPlanType(e.target.value)}
            options={[
              { value: "premium", name: "プレミアム" },
              { value: "selection", name: "セレクション" }
            ]}
            show={false}
          />

          <InputFormLayout
            id="Referrer"
            labelTitle="リファラ元URL（カンマ区切りで複数入力可能）"
            type="text"
            value={referrerUrls}
            onChange={(e) => setReferrerUrls(e.target.value)}
            placeholder="例: https://example.com, https://test.com"
            required="false"
          />

          <SelectLayout
            id="isMember"
            selectTitle="会員ID設定"
            value={isMember}
            onChange={(e) => {
              setIsMember(e.target.value);
              if (e.target.value === '0') {
                setMemberIdValidationType('');
                setMemberIdMaxDigits('');
                setMemberIdMinDigits('');
                setMemberIdLabel('');
              }
              if (e.target.value === '2') {
                // 自動の場合、バリデーションタイプはクリア（POST連携時にバリデーション済み）
                setMemberIdValidationType('');
              }
            }}
            options={[
              { value: "0", name: "なし" },
              { value: "1", name: "手動" },
              { value: "2", name: "自動" }
            ]}
            show={false}
          />

          {/* 手動(1)・自動(2)の場合、共通の設定フィールドを表示 */}
          {(isMember === '1' || isMember === '2') && (
            <>
              <InputFormLayout
                  id="memberIdLabel"
                  labelTitle="会員ID入力フォームの注釈"
                  type="text"
                  value={memberIdLabel}
                  onChange={(e) => setMemberIdLabel(e.target.value)}
                  placeholder="例: 会員IDを入力・確認してボタンを押下してください。"
                  multiline={true}
              />

              {/* バリデーションタイプは手動(1)の場合のみ表示 */}
              {isMember === '1' && (
                <SelectLayout
                  id="memberIdValidationType"
                  selectTitle="バリデーションチェックNo"
                  value={memberIdValidationType}
                  onChange={(e) => setMemberIdValidationType(e.target.value)}
                  options={validationTypeOptions.map(option => ({
                    id: option.value,
                    name: option.label,
                    value: option.value
                  }))}
                />
              )}

              <InputFormLayout
                id="memberIdMinDigits"
                labelTitle="有効桁数（最小）"
                type="number"
                min="1"
                value={memberIdMinDigits}
                onChange={(e) => setMemberIdMinDigits(e.target.value)}
                placeholder="例: 1"
              />

              <InputFormLayout
                id="memberIdMaxDigits"
                labelTitle="有効桁数（最大）"
                type="number"
                min="1"
                value={memberIdMaxDigits}
                onChange={(e) => setMemberIdMaxDigits(e.target.value)}
                placeholder="例: 20"
              />
            </>
          )}
          
          <div style={{ marginTop: "30px" }} />
          <RegistarButtonLayout
            buttonTitle="登録"
            type="submit"
            onClick={handleSubmit}
          />

          <RegistarButtonLayout
            buttonTitle="企業一覧に戻る"
            type="button"
            onClick={() => navigate('/campaignlist')}
          />

          <RegistarButtonLayout
            buttonTitle="企業ポータルに戻る"
            type="button"
            onClick={() => navigate('/implpage')}
          />
      </RegistarLayout>
  );
};

export default CampaignRegistration;
